import { useState } from 'react';
import { useIntl } from 'react-intl';
import { SupportedLocale } from '../i18n';
import { useIntlContext } from '../contexts/intl-context';
import { useChangeLocale } from '../hooks/useLocale';
import flagFr from '../assets/icons/svg/flag-fr.svg';
import flagEn from '../assets/icons/svg/flag-en.svg';
import flagEs from '../assets/icons/svg/flag-es.svg';
import flagPt from '../assets/icons/svg/flag-pt.svg';
import flagNl from '../assets/icons/svg/flag-nl.svg';

interface LanguageSelectorProps {
  /** Optional controlled API (e.g. Human badge). Defaults to global locale context. */
  currentLocale?: SupportedLocale;
  onLanguageChange?: (locale: SupportedLocale) => void;
  className?: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  currentLocale: controlledLocale,
  onLanguageChange,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const intl = useIntl();
  const { locale: contextLocale } = useIntlContext();
  const changeLocale = useChangeLocale();

  const currentLocale = controlledLocale ?? contextLocale;

  const languages = [
    { code: 'fr' as SupportedLocale, flag: flagFr, label: intl.formatMessage({ id: 'common.languageFrench' }) },
    { code: 'en' as SupportedLocale, flag: flagEn, label: intl.formatMessage({ id: 'common.languageEnglish' }) },
    { code: 'es' as SupportedLocale, flag: flagEs, label: intl.formatMessage({ id: 'common.languageSpanish' }) },
    { code: 'pt' as SupportedLocale, flag: flagPt, label: intl.formatMessage({ id: 'common.languagePortuguese' }) },
    { code: 'nl' as SupportedLocale, flag: flagNl, label: intl.formatMessage({ id: 'common.languageDutch' }) },
  ];

  const currentLanguage = languages.find((lang) => lang.code === currentLocale) || languages[0];

  const handleLanguageSelect = (locale: SupportedLocale) => {
    if (onLanguageChange) {
      onLanguageChange(locale);
    } else {
      changeLocale(locale);
    }
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-white/80 backdrop-blur-sm rounded-lg shadow-sm hover:bg-white/90 transition-colors border border-gray-200"
        aria-label={intl.formatMessage({ id: 'common.languageLabel' })}
      >
        <img src={currentLanguage.flag} alt={currentLanguage.label} className="w-5 h-4" />
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full mt-1 right-0 bg-white rounded-lg shadow-lg border border-gray-200 min-w-[140px] z-20">
            {languages.map((language) => (
              <button
                type="button"
                key={language.code}
                onClick={() => handleLanguageSelect(language.code)}
                className={`flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                  currentLocale === language.code ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'
                }`}
              >
                <img src={language.flag} alt={language.label} className="w-5 h-4" />
                <span className="text-sm font-medium">{language.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSelector;
